/**
 * SessionContext - Shared Session State Management
 *
 * Provides shared session state across all components via React Context.
 * This fixes the state isolation issue where useSession() had separate
 * useState instances per component.
 *
 * Features:
 * - In-memory token storage (NOT localStorage for security)
 * - Token expiration checking
 * - Automatic refresh when < 5 minutes remaining
 * - Multi-tab synchronization via BroadcastChannel
 *
 * @see specs/002-frontend-mvp/spec.md - User Story 1
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { Session } from '../../../types/session';

// ============================================================================
// Context Types
// ============================================================================

interface SessionContextValue {
  session: Session | null;
  token: string | null;
  refreshToken: string | null;
  isExpired: boolean;
  needsRefresh: () => boolean;
  setToken: (session: Session) => void;
  clearToken: () => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const SessionContext = createContext<SessionContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const intervalRef = useRef<number | null>(null);

  /**
   * Initialize BroadcastChannel for multi-tab synchronization
   */
  useEffect(() => {
    // Only create BroadcastChannel if supported (not in all environments)
    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel('auth-sync');

      // Listen for logout events from other tabs
      channelRef.current.onmessage = (event) => {
        if (event.data.type === 'logout') {
          setSession(null);
          setIsExpired(true);
        } else if (event.data.type === 'token-refresh' && event.data.session) {
          setSession(event.data.session);
          setIsExpired(false);
        }
      };
    }

    return () => {
      channelRef.current?.close();
    };
  }, []);

  /**
   * Check token expiration every 30 seconds
   */
  useEffect(() => {
    if (!session) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const checkExpiration = () => {
      if (!session) return;

      const expiresAt = new Date(session.expiresAt).getTime();
      const now = Date.now();
      const timeRemaining = expiresAt - now;

      // Check if expired
      if (timeRemaining <= 0) {
        setIsExpired(true);
        setSession(null);
      } else {
        setIsExpired(false);
      }
    };

    // Check immediately
    checkExpiration();

    // Check every 30 seconds
    intervalRef.current = window.setInterval(checkExpiration, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [session]);

  /**
   * Set session token (after login or refresh)
   */
  const setToken = useCallback((newSession: Session) => {
    setSession(newSession);
    setIsExpired(false);

    // Broadcast to other tabs
    if (channelRef.current) {
      channelRef.current.postMessage({
        type: 'token-refresh',
        session: newSession,
      });
    }
  }, []);

  /**
   * Clear session token (on logout)
   */
  const clearToken = useCallback(() => {
    setSession(null);
    setIsExpired(true);

    // Broadcast logout to other tabs
    if (channelRef.current) {
      channelRef.current.postMessage({
        type: 'logout',
      });
    }
  }, []);

  /**
   * Check if token needs refresh (< 5 minutes remaining)
   */
  const needsRefresh = useCallback((): boolean => {
    if (!session) return false;

    const expiresAt = new Date(session.expiresAt).getTime();
    const now = Date.now();
    const timeRemaining = expiresAt - now;
    const fiveMinutes = 5 * 60 * 1000;

    return timeRemaining < fiveMinutes && timeRemaining > 0;
  }, [session]);

  /**
   * Get current token
   */
  const token = session?.token ?? null;

  /**
   * Get refresh token
   */
  const refreshToken = session?.refreshToken ?? null;

  const value: SessionContextValue = {
    session,
    token,
    refreshToken,
    isExpired,
    needsRefresh,
    setToken,
    clearToken,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useSessionContext - Access shared session state
 *
 * @throws Error if used outside of SessionProvider
 */
export function useSessionContext(): SessionContextValue {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }

  return context;
}

export default SessionContext;
